import { Set, Map } from 'immutable';
import Contact from '@repo/utils/models/Contact';
import type { ID, ReduxAction, SearchState } from '@repo/utils/types';
import {
  fetchSearchResultsFailed,
  fetchSearchResultsRequested,
  fetchSearchResultsSucceeded,
  initialSearchState,
} from './search/createSearchReducer';
import AppState from '../app-state';
import Occasion from '@repo/utils/helpers/Occasion';

export const selectArchiveSearch = (state: AppState) => state.data.archiveSearch;
export const selectArchivedContact = (id: ID) => (state: AppState) =>
  selectArchiveSearch(state).archivedContacts.get(id);

const setFetchingStatusForBatch =
  ({ contactIDs, isFetching }: { contactIDs: Set<ID>; isFetching: boolean }) =>
  (contact: Contact, id: ID) => {
    if ((contactIDs || Set()).has(id)) {
      return contact.copyWith({ isFetching });
    } else {
      return contact;
    }
  };

const initialArchiveState = {
  ...initialSearchState('archiveSearch', { sorting: { name: 'archive_date' } }),
  archivedContacts: Map<ID, Contact>(),
};

export type ArchiveSearchState = SearchState & { archivedContacts: Map<ID, Contact> };

/*
TODO:
- Also handle contact delete (archive action) by removing those IDs from the "deleted IDs" list here
- Ensure that restoring contacts updates this cache accordingly
*/
export function archiveSearchReducer(
  state: ArchiveSearchState = initialArchiveState,
  { type, payload }: ReduxAction,
) {
  switch (type) {
    case 'SET_ARCHIVE_SEARCH_TERM':
      return { ...state, term: payload.term };

    case 'FETCH_ARCHIVE_SEARCH_RESULTS_REQUESTED':
      return fetchSearchResultsRequested(state);

    case 'FETCH_ARCHIVE_SEARCH_RESULTS_SUCCEEDED':
      return {
        ...fetchSearchResultsSucceeded(state, payload),
        archivedContacts: state.archivedContacts.merge(
          payload.response.hits.hits.map(({ _source: archivedContact }) => [
            String(archivedContact.id),
            new Contact(archivedContact),
          ]),
        ),
      };

    case 'FETCH_ARCHIVE_SEARCH_RESULTS_FAILED':
      return fetchSearchResultsFailed(state, payload);

    case 'FETCH_ARCHIVED_CONTACT_SUCCEEDED':
      return {
        ...state,
        archivedContacts: state.archivedContacts.set(
          payload.contactID,
          new Contact(payload.response),
        ),
      };

    case 'RESTORE_ARCHIVED_CONTACT_BATCH_REQUESTED':
      return {
        ...state,
        archivedContacts: state.archivedContacts.map(
          setFetchingStatusForBatch({ contactIDs: payload.contactIDs, isFetching: true }),
        ),
      };

    case 'RESTORE_ARCHIVED_CONTACT_BATCH_SUCCEEDED':
      return {
        ...state,
        results: state.results.copyWith({
          deletedIDs: state.results.deletedIDs.union(payload.contactIDs),
          updated: new Occasion(),
        }),
      };

    case 'RESTORE_ARCHIVED_CONTACT_BATCH_FAILED':
      return {
        ...state,
        archivedContacts: state.archivedContacts.map(
          setFetchingStatusForBatch({ contactIDs: payload.contactIDs, isFetching: false }),
        ),
      };

    case 'DELETE_CONTACT_SUCCEEDED':
      return {
        ...state,
        results: state.results.copyWith({
          deletedIDs: state.results.deletedIDs.delete(payload.contactID),
          updated: new Occasion(),
        }),
      };

    case 'DELETE_CONTACT_BATCH_SUCCEEDED':
      return {
        ...state,
        results: state.results.copyWith({
          deletedIDs: state.results.deletedIDs.subtract(payload.contactIDs),
          updated: new Occasion(),
        }),
      };

    default:
      return state;
  }
}
